import React, { Component } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import { injectIntl, defineMessages, FormattedMessage, FormattedDate } from 'react-intl';
import PropTypes from 'prop-types';
import intlShape from '@/shared/utils/intlShape';
import ScrollableTable from '@/shared/components/table/ScrollableTable';
import ColumnHeader from '@/shared/components/table/ColumnHeader';
import Button from '@/shared/components/Button';
import config from '@/config';
import api from '@/shared/api';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import FormattedListValue from '@/shared/components/FormattedListValue';
import RelationSearch from '@/shared/components/input/RelationSearch';
import isEqual from 'lodash-es/isEqual';
import TextSearch from '@/shared/components/input/TextSearch';
import Axios from 'axios';
import RelationName from '@/shared/components/RelationName';
import FileIcon from '@/shared/components/FileIcon';
import DocumentTreeView from '@/shared/components/DocumentTreeView';
import DocumentSigningStatusIcon from '@/shared/components/DocumentSigningStatusIcon';
import FormattedDateShort from './FormattedDateShort';
import enumCache from '@/shared/services/EnumCacheService';
import { dynamicSort } from '@/shared/utils/dynamicSort';

import MultiSelect, {
  addItem as addMultiSelectItem,
  removeItem as removeMultiSelectItem,
  splitItems as splitMultiSelectItems,
} from '@/shared/components/input/multiselect/MultiSelect';
import { listItemMsg } from '@/shared/components/FormattedListValue';

const tagsToString = tags => (tags ? tags.sort().join(';') : null);

const messages = defineMessages({
  DocumentType: 'Document type',
  Save: 'Save',
  Cancel: 'Cancel',
  DocumentSelection: 'Document selection',
  NoDocumentsFound: 'No documents found',
  Name: 'Name',
  Type: 'Type',
  Relation: 'Relation',
  DocumentDate: 'Document date',
  Add: 'Add',
  SearchDocuments: 'Search documents',
  Delete: 'Delete',
  Ready: 'Ready',
  NrOfLinkedRelations: '{nr} relations',
  LinkType: 'Link type',
  Selected: 'Selected',
});

class DocumentSelectorModal extends Component {
  static propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    intl: intlShape.isRequired,
    initialRelation: PropTypes.string,
    initialLinkTypes: PropTypes.arrayOf(PropTypes.number),
    initialSelectedDocuments: PropTypes.arrayOf(PropTypes.object),
  };

  static defaultProps = {
    show: false,
    disabled: false,
    initialRelation: undefined,
    initialLinkTypes: [],
    initialSelectedDocuments: [],
  };

  constructor(props) {
    super(props);

    const { initialRelation, initialLinkTypes, initialSelectedDocuments } = props;
    let initialFilters = {};
    if (initialRelation) {
      initialFilters = {
        ...initialFilters,
        relationId: initialRelation,
      };
    }

    this.state = {
      selectedDocuments: initialSelectedDocuments || [],
      selectedDocumentTypes: [],
      selectedLinkTypes: initialLinkTypes || [],
      documentTypes: [],
      linkTypes: [],
      documents: [],
      treeviewReady: false,
      initialSearchParams: {
        sortKey: 'documentDate',
        desc: true,
        pageSize: 50,
        mainFilters: initialFilters,
      },
    };
  }

  componentDidMount() {
    const { filters, intl } = this.props;

    if (filters && filters.period) {
      this.setPeriod(filters.period);
    }
    Promise.all([enumCache.get('documenttype'), enumCache.get('linktype')]).then(
      ([documentTypes, linkTypes]) => {
        this.setState({
          documentTypes: documentTypes
            .map(item => ({
              id: item.key,
              label: intl.formatMessage(listItemMsg('documenttype', item.key, item.value)),
            }))
            .sort(dynamicSort('label')),
          linkTypes: linkTypes
            .map(item => ({
              id: item.key,
              label: intl.formatMessage(listItemMsg('linktypeto', item.key, item.value)),
            }))
            .sort(dynamicSort('label')),
        });
      },
    );
  }

  componentWillUnmount() {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }
  onTreeviewLoaded = ({ tags }) => {
    this.setState(state => ({
      ...state,
      treeviewReady: true,
      initialSearchParams: {
        ...state.initialSearchParams,
        mainFilters: { ...state.initialSearchParams.mainFilters },
        tags: tagsToString(tags),
        page: 0,
        f: new Date().getTime(),
      },
    }));
  };

  filterDocumentsByTags = (tags, force) => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        tags: tagsToString(tags),
        page: 0,
        f: force ? new Date().getTime() : undefined,
      },
    }));
  };

  relationChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ relationId: null, relationName: null });
    } else {
      this.filterChanged({
        relationId: item.key,
        relationName: item.label,
      });
    }
  };

  filterChanged(filters) {
    const { initialSearchParams } = this.state;
    if (isEqual(initialSearchParams.mainFilters, filters)) {
      return;
    }

    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        mainFilters: { ...state.initialSearchParams.mainFilters, ...filters },
      },
    }));
  }

  onDocumentTypeFilterAdded = selectedItem => {
    if (!selectedItem) return;

    const { filters } = this.props;
    const { selectedDocumentTypes = [] } = this.state;

    if (selectedDocumentTypes.includes(selectedItem.id)) return;
    this.setState(
      {
        selectedDocumentTypes: [...selectedDocumentTypes, selectedItem.id],
      },
      () => {
        this.setState(state => ({
          ...state,
          initialSearchParams: {
            ...state.initialSearchParams,
            page: 0,
            f: new Date().getTime(),
          },
        }));
      },
    );
  };
  onDocumentTypeFilterRemoved = item => {
    const { filters } = this.props;
    const { selectedDocumentTypes = [] } = this.state;
    const index = selectedDocumentTypes.findIndex(x => x === item.value.id);
    const copy = [...selectedDocumentTypes];
    copy.splice(index, 1);
    this.setState({ selectedDocumentTypes: copy }, () => {
      this.setState(state => ({
        ...state,
        initialSearchParams: {
          ...state.initialSearchParams,
          page: 0,
          f: new Date().getTime(),
        },
      }));
    });
  };

  onLinkTypeFilterAdded = selectedItem => {
    if (!selectedItem) return;

    const { selectedLinkTypes = [] } = this.state;

    if (selectedLinkTypes.includes(selectedItem.id)) return;
    this.setState({ selectedLinkTypes: [...selectedLinkTypes, selectedItem.id] }, () => {
      this.setState(state => ({
        ...state,
        initialSearchParams: {
          ...state.initialSearchParams,
          page: 0,
          f: new Date().getTime(),
        },
      }));
    });
  };

  onLinkTypeFilterRemoved = item => {
    const { selectedLinkTypes = [] } = this.state;
    const index = selectedLinkTypes.findIndex(x => x === item.value.id);
    const copy = [...selectedLinkTypes];
    copy.splice(index, 1);
    this.setState({ selectedLinkTypes: copy }, () => {
      this.setState(state => ({
        ...state,
        initialSearchParams: {
          ...state.initialSearchParams,
          page: 0,
          f: new Date().getTime(),
        },
      }));
    });
  };

  loadDocuments = ({ tags, desc, sortKey: sort, page, pageSize: take, mainFilters = {} }) => {
    const { relationId = null, search = null } = mainFilters;
    const params = {
      desc,
      sort,
      page,
      take,
      tags,
      search,
    };
    const { selectedDocuments, selectedDocumentTypes, selectedLinkTypes } = this.state;

    if (relationId) {
      params.linkId = relationId;
      params.linkType = 3; // link type 3 = relation
    }

    params.documentTypes = selectedDocumentTypes;
    params.linkTypes = selectedLinkTypes;

    if (typeof this.cancelToken !== 'undefined') {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    this.cancelToken = Axios.CancelToken.source();

    return api.base
      .post(`${config.appUrls.documents}/api/documents/GetAll`, params, {
        cancelToken: this.cancelToken.token,
      })
      .then(({ data }) => {
        const newDocuments = data.documents.map(e => ({
          ...e,
          selected: selectedDocuments.map(x => x.id).includes(e.id),
        }));

        this.setState(state => ({
          ...state,
          documents: page > 0 ? [...state.documents, ...newDocuments] : [...newDocuments],
          currentSearchParams: params,
        }));

        return data.documents;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  };
  renderRow = document => {
    const { intl } = this.props;

    return (
      <tr key={document.id}>
        <td className="">
          <FileIcon document={document} />
          <span>{document.name}</span>
        </td>
        <td className="">
          <FormattedListValue list="documenttype" id={document.documentType} />
        </td>
        <td className="">
          {document.relations &&
            document.relations.slice(0, 3).map(relation => (
              <span className="margin10R" key={relation.id}>
                <RelationName key={relation.id} relation={relation} />
              </span>
            ))}
          {document.relations && document.relations.length > 3 && (
            <span
              title={intl.formatMessage(messages.NrOfLinkedRelations, {
                nr: document.relations.length,
              })}
            >
              &hellip;
            </span>
          )}
        </td>
        <td>
          <FormattedDateShort value={document.documentDate} />
        </td>
        <td className="col-xs-1 ">
          {(document.shouldBeSigned || document.signingStatus > 1) && document.signingStatus && (
            <DocumentSigningStatusIcon document={document} />
          )}
        </td>
        <td className="col-xs-2 text-right ">
          {document.selected ? (
            <Button
              onClick={() =>
                this.setState(state => ({
                  ...state,
                  selectedDocuments: state.selectedDocuments.filter(x => x.id !== document.id),
                  documents: state.documents.map(x =>
                    x.id === document.id ? { ...x, selected: !x.selected } : x,
                  ),
                }))
              }
              btnClassName="btn-xs"
            >
              {intl.formatMessage(messages.Delete)}
            </Button>
          ) : (
            <Button
              onClick={() =>
                this.setState(state => ({
                  ...state,
                  selectedDocuments: [...state.selectedDocuments, document],
                  documents: state.documents.map(x =>
                    x.id === document.id ? { ...x, selected: !x.selected } : x,
                  ),
                }))
              }
              btnClassName="btn-xs"
            >
              {intl.formatMessage(messages.Add)}
            </Button>
          )}
        </td>
      </tr>
    );
  };

  render() {
    const { show, onClose, intl, onDocumentsSelected, initialRelation } = this.props;
    const {
      selectedDocuments,
      treeviewReady,
      initialSearchParams,
      documents,
      selectedDocumentTypes,
      documentTypes,
      selectedLinkTypes,
      linkTypes,
    } = this.state;
    const { mainFilters = {} } = initialSearchParams;

    let initRelation = null;
    if (mainFilters.relationId) {
      initRelation = {
        key: mainFilters.relationId,
        // label will get retrieved by search component
      };
    }

    const selectedDocumentTypeObjects =
      documentTypes && selectedDocumentTypes
        ? documentTypes.filter(r => selectedDocumentTypes.includes(r.id))
        : [];
    const selectedLinkTypeObjects =
      linkTypes && selectedLinkTypes ? linkTypes.filter(r => selectedLinkTypes.includes(r.id)) : [];
    return (
      <Modal show={show} onHide={onClose} dialogClassName="modal-xl document-selector-modal">
        <Modal.Header closeButton>
          {document && (
            <Modal.Title>
              <FormattedMessage {...messages.DocumentSelection} />
            </Modal.Title>
          )}
        </Modal.Header>
        <Modal.Body>
          {
            <div className="row">
              <div className="col-md-3 " style={{ height: '500px', overflow: 'auto' }}>
                <div>
                  <RelationSearch
                    onChange={this.relationChanged}
                    initialSelectedItem={initRelation}
                  />
                </div>
                <div className="margin10T">
                  <TextSearch
                    className="form-control"
                    placeholder={intl.formatMessage(messages.SearchDocuments)}
                    onChange={value => this.filterChanged({ search: value })}
                  />
                </div>
                <div className="margin10T">
                  <MultiSelect
                    selectedItem={selectedDocumentTypeObjects}
                    onItemRemoved={this.onDocumentTypeFilterRemoved}
                    onItemSelected={this.onDocumentTypeFilterAdded}
                    items={documentTypes}
                    placeholder={intl.formatMessage(messages.Type)}
                  />
                </div>
                <div className="margin10T">
                  <MultiSelect
                    selectedItem={selectedLinkTypeObjects}
                    onItemRemoved={this.onLinkTypeFilterRemoved}
                    onItemSelected={this.onLinkTypeFilterAdded}
                    items={linkTypes}
                    placeholder={intl.formatMessage(messages.LinkType)}
                    disabled={!mainFilters.relationId}
                  />
                </div>
                <div className="margin10T" />
                <DocumentTreeView
                  allowUpload={false}
                  onFilterChanged={({ tags, force }) => this.filterDocumentsByTags(tags, force)}
                  onTreeLoaded={this.onTreeviewLoaded}
                />
              </div>

              <div className="col-md-7 border-left">
                {treeviewReady ? (
                  <div
                    ref={ref => (this.scrollParentRef = ref)}
                    // this is required to make the scroll component work in modal
                    style={{ height: '500px', overflow: 'auto' }}
                  >
                    <ScrollableTable
                      initialSearchParams={initialSearchParams}
                      items={documents}
                      useWindow={false}
                      tableClassName="table-fixed margin50B"
                      noDataLabel={intl.formatMessage(messages.NoDocumentsFound)}
                      loadItems={searchParams => this.loadDocuments(searchParams)}
                      renderRow={item => this.renderRow(item)}
                      renderTBody
                      scrollParentRef={this.scrollParentRef}
                    >
                      <ColumnHeader
                        title={intl.formatMessage(messages.Name)}
                        className="file-name-column text-nowrap"
                        sortKey="fileName"
                      />
                      <ColumnHeader
                        title={intl.formatMessage(messages.Type)}
                        sortKey="documentType"
                        className="type-column text-nowrap"
                      />
                      <ColumnHeader
                        title={intl.formatMessage(messages.Relation)}
                        className="relation-column text-nowrap"
                      />
                      <ColumnHeader
                        title={intl.formatMessage(messages.DocumentDate)}
                        sortKey="documentDate"
                        className="document-date-column text-nowrap"
                      />
                      <ColumnHeader className="extra-icon-column col-xs-1 text-nowrap" />
                      <ColumnHeader title="" className="actions-column col-xs-2 text-right" />
                    </ScrollableTable>
                  </div>
                ) : (
                  <LoadingAnimation />
                )}
              </div>

              <div className="col-md-2 border-left" style={{ height: '500px', overflow: 'auto' }}>
                <h5>
                  <FormattedMessage {...messages.Selected} /> ({selectedDocuments.length})
                </h5>
                <ul className="list-unstyled">
                  {selectedDocuments.map(doc => (
                    <li
                      key={doc.id}
                      className="margin5B"
                      style={{ borderBottom: '1px solid #eee', paddingBottom: '5px' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <FileIcon document={doc} />
                          <span title={doc.name}>{doc.name}</span>
                        </div>
                        <Button
                          onClick={() =>
                            this.setState(state => ({
                              ...state,
                              selectedDocuments: state.selectedDocuments.filter(
                                x => x.id !== doc.id,
                              ),
                              documents: state.documents.map(x =>
                                x.id === doc.id ? { ...x, selected: !x.selected } : x,
                              ),
                            }))
                          }
                          btnClassName="btn-xs"
                        >
                          <i className="fa fa-times" />
                        </Button>
                      </div>
                      <div className="text-muted small">
                        <FormattedDateShort value={doc.documentDate} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          }
        </Modal.Body>
        <Modal.Footer>
          <div style={{ float: 'right' }}>
            <Button onClick={onClose}>{intl.formatMessage(messages.Cancel)}</Button>
            <Button
              onClick={() => onDocumentsSelected(selectedDocuments)}
              disabled={selectedDocuments.length <= 0}
              btnClassName="btn-inverse"
            >
              {intl.formatMessage(messages.Ready)}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default injectIntl(DocumentSelectorModal);
